import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import Dexie, { Table } from 'dexie';
import { environment } from 'src/environments/environment';
import { LocalDatabaseService } from './local-database.service';

interface CollectionDelta {
  upserts: Record<string, unknown>[];
  deletes: string[];
}

interface DeltaSyncResponse {
  serverTime: string;
  fullResync: boolean;
  deltas: Record<string, CollectionDelta>;
}

interface CollectionConfig {
  key: string;
  table: Table<any, string>;
  usesIdKey?: boolean;
}

@Injectable({ providedIn: 'root' })
export class DeltaSyncService {
  private readonly collections: CollectionConfig[];

  constructor(
    private http: HttpClient,
    private localDb: LocalDatabaseService,
  ) {
    this.collections = [
      { key: 'boundBooks', table: this.localDb.boundBooks },
      { key: 'firearms', table: this.localDb.firearms, usesIdKey: true },
      { key: 'ffls', table: this.localDb.ffls, usesIdKey: true },
      { key: 'vendors', table: this.localDb.vendors, usesIdKey: true },
      { key: 'customers', table: this.localDb.customers, usesIdKey: true },
      { key: 'productsServices', table: this.localDb.productsServices, usesIdKey: true },
      { key: 'salesReceipts', table: this.localDb.salesReceipts, usesIdKey: true },
      { key: 'expenseRecords', table: this.localDb.expenseRecords, usesIdKey: true },
      { key: 'form4473s', table: this.localDb.form4473s },
      { key: 'reservedFirearms', table: this.localDb.reservedFirearms },
      { key: 'reserved4473s', table: this.localDb.reserved4473s, usesIdKey: true },
      { key: 'calendarEvents', table: this.localDb.calendarEvents, usesIdKey: true },
    ];
  }

  get enabled(): boolean {
    return environment.enableDeltaSync;
  }

  async sync(companyId: string): Promise<void> {
    const metadataId = `delta-sync:${companyId}`;
    const metadata = await this.localDb.syncMeta.get(metadataId);
    const params = metadata?.lastSyncedAt
      ? new HttpParams().set('since', metadata.lastSyncedAt)
      : undefined;
    const response = await firstValueFrom(
      this.http.get<DeltaSyncResponse>(`${environment.apiUrl}/delta_sync`, { params }),
    );

    await this.localDb.transaction(
      'rw',
      this.collections.map(({ table }) => table),
      async () => {
        for (const config of this.collections) {
          const delta = response.deltas[config.key] || { upserts: [], deletes: [] };
          if (response.fullResync) {
            await config.table.where('company_id').equals(companyId).delete();
          }
          const upserts = delta.upserts.map((record) => this.normalizeRecord(record, config));
          if (upserts.length > 0) await config.table.bulkPut(upserts);
          if (delta.deletes.length > 0) await config.table.bulkDelete(delta.deletes);
        }
      },
    );

    // A failed metadata write deliberately retries this idempotent window next time.
    await this.localDb.syncMeta.put({
      id: metadataId,
      company_id: companyId,
      lastSyncedAt: response.serverTime,
    });
  }

  private normalizeRecord(record: Record<string, unknown>, config: CollectionConfig): Record<string, unknown> {
    if (!config.usesIdKey || record['id'] || !record['_id']) return record;
    return { ...record, id: String(record['_id']) };
  }
}